import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { eur, lineUnitPrice } from "@/lib/format";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed top-0 right-0 z-[61] h-full w-full max-w-md ee-glass border-l ee-hairline flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="flex items-center justify-between px-7 py-6 border-b ee-hairline">
              <h2 className="font-display text-2xl tracking-wide">Your Cart ({count})</h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" aria-label="Close cart">
                <X strokeWidth={1} className="text-[#A8A29E] hover:text-[#D4AF37]" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
              {items.length === 0 && (
                <p className="text-[#A8A29E] text-center mt-16" data-testid="cart-empty">
                  Your cart is empty.
                </p>
              )}
              {items.map((it, i) => (
                <div key={i} className="flex gap-4" data-testid={`cart-item-${i}`}>
                  <div className="h-24 w-20 bg-[#1C1917] overflow-hidden flex-shrink-0">
                    <img src={it.product.images?.[0]} alt={it.product.name} className="h-full w-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-lg leading-tight">{it.product.name}</p>
                    {it.variant_index != null && it.product.variants?.[it.variant_index] && (
                      <p className="text-xs text-[#A8A29E] mt-1">
                        {it.product.variants[it.variant_index].name}
                      </p>
                    )}
                    <p className="ee-gold-text text-sm mt-1">{eur(lineUnitPrice(it))}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => updateQty(i, it.quantity - 1)} data-testid={`cart-dec-${i}`} className="border ee-hairline p-1 hover:border-[#D4AF37]">
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-5 text-center">{it.quantity}</span>
                      <button onClick={() => updateQty(i, it.quantity + 1)} data-testid={`cart-inc-${i}`} className="border ee-hairline p-1 hover:border-[#D4AF37]">
                        <Plus size={12} />
                      </button>
                      <button onClick={() => removeItem(i)} data-testid={`cart-remove-${i}`} className="ml-auto text-[#A8A29E] hover:text-[#D4AF37]">
                        <Trash2 size={16} strokeWidth={1} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t ee-hairline px-7 py-6">
                <div className="flex justify-between mb-1">
                  <span className="text-[#A8A29E]">Subtotal</span>
                  <span className="font-display text-2xl ee-gold-text" data-testid="cart-subtotal">
                    {eur(subtotal)}
                  </span>
                </div>
                <p className="text-xs text-[#57534E] mb-5">Excl. VAT · Shipping calculated at checkout</p>
                <button
                  data-testid="cart-checkout-btn"
                  onClick={() => {
                    setOpen(false);
                    navigate("/cart");
                  }}
                  className="w-full bg-[#D4AF37] text-[#0C0A09] py-4 uppercase tracking-[0.15em] text-sm hover:bg-[#B5952F] transition-colors"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
